import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { toast } from 'src/hooks/useToast';
import { useCreateUser } from 'src/features/users/api/useCreateUser';
import { userMessages } from 'src/features/users/utils/messages';
import { createUserFormSchema, CreateUserFormSchemaValues } from 'src/features/users/utils/formSchema';
import CreateUserForm from 'src/features/users/components/forms/CreateUserForm';
import { ICON_MAP } from 'src/utils/constants';
import { LIST_USERS_RESOURCE } from 'src/features/users/api/useListUsers';

const defaultValues = {
    name: '',
    username: '',
    roles: [],
    email: '',
    password: '',
    password_confirm: '',
};
function CreateUserFormModal<TData>({ className, semantics }: DataTableNewEntryProps<TData>) {
    const { formatMessage } = useIntl();

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onError = useOnError();
    const { mutate, isPending } = useCreateUser({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_USERS_RESOURCE] });
            toast({
                title: formatMessage(userMessages.createUserSuccess),
                description: formatMessage(userMessages.createUserSuccessDescription, {
                    entry: success.name,
                }),
            });
            setIsModalOpen(false);
        },
        onError: (error) => onError(error, form.setError),
    });

    const form = useForm<CreateUserFormSchemaValues>({
        resolver: zodResolver(createUserFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const title = formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() });

    const onSubmit = React.useCallback((values: CreateUserFormSchemaValues) => mutate(values), [mutate]);

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button className={cn('ml-auto', className)}>
                    <FontAwesomeIcon icon={ICON_MAP.ADD} />
                    {title}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.ADD} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{formatMessage(userMessages.addUserSubtitle)}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <CreateUserForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateUserFormModal;
